// Total que paga el cliente en cada forma de pago según qué promos admite cada una.
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCatalogLineInfo } from '@/domains/sales/wizard/api/useCatalogLineInfo';
import { anchorLineOf } from '@/domains/sales/wizard/buildSale';
import type { ReservedLine, WizardSession } from '@/domains/sales/wizard/types';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';
import { cachedEvaluation, useStoreSalePromotions } from '../api/useWizardPromotions';
import {
    heldCodeIsSureKeepOnly,
    heldCodePromotionOf,
    releasedPromotionsOf,
    salePromotionsOf,
    trackTotalCents,
    type PaymentTrack,
    type PromotionFacts,
} from '../lib/paymentOptionPromotions';
import { isSureKeepOnly } from '../lib/promotionAppliesTo';
import { rememberedPromotionName, rememberedSureKeepOnly } from '../lib/promotionNames';
import { releasedFor } from '../lib/sureKeepRelease';
import { useSessionPromotionState } from '../store/promotionSelectionStore';
import { afterSaleText } from '../components/AfterSaleBenefits';

/** Nombre, alcance y lo que entrega de cada promo, con lo que ya está en caché. */
export function usePromotionFacts(sessionId: string): (promotionId: string) => PromotionFacts {
    const queryClient = useQueryClient();
    const store = useStoreSalePromotions(sessionId).data?.items ?? [];
    const evaluation = cachedEvaluation(sessionId, queryClient);
    return (promotionId) => {
        const listed = store.find((p) => p.promotionId === promotionId);
        const evaluated = evaluation?.promotions.find((p) => p.promotionId === promotionId);
        const known = listed ?? evaluated;
        const afterSale = (evaluated?.afterSale ?? [])
            .map(afterSaleText)
            .filter(Boolean)
            .join(' · ');
        return {
            name:
                listed?.name ??
                evaluated?.name ??
                rememberedPromotionName(sessionId, promotionId) ??
                'Promo de venta',
            sureKeepOnly: known
                ? isSureKeepOnly(known)
                : rememberedSureKeepOnly(sessionId, promotionId),
            text: afterSale !== '' ? afterSale : (listed?.customerText ?? ''),
            ...(known?.paymentOptions !== undefined && { paymentOptions: known.paymentOptions }),
            categories: listed?.scope.productCategories ?? null,
        };
    };
}

/** Lo que baja el código según la evaluación en caché, si aplica. */
export function evaluatedCodeCents(
    queryClient: QueryClient,
    sessionId: string,
    code: string,
): number | undefined {
    const evaluated = cachedEvaluation(sessionId, queryClient)?.discountCode;
    return evaluated?.applicable && evaluated.codeText === code
        ? evaluated.totalDiscountCents
        : undefined;
}

/** Total de la venta en una forma de pago: sin las promos que esa forma no admite. */
export function usePaymentTrackTotal(
    sessionId: string,
    session: WizardSession,
    track: PaymentTrack,
    saleTotalCents: number,
): number {
    const queryClient = useQueryClient();
    const facts = usePromotionFacts(sessionId);
    const { descriptions } = useCatalogLineInfo(sessionId);
    const { sureKeepReleased, anyFinancierCodes = [] } = useSessionPromotionState(sessionId);
    const released = releasedFor(sureKeepReleased, anchorLineOf(session)?.lineId);
    const productName = (line: ReservedLine): string =>
        catalogDisplayName({ sku: line.sku, ...line.product }, descriptions);
    const code = heldCodePromotionOf(session, {
        sureKeepOnly: (c) => heldCodeIsSureKeepOnly(session, c, anyFinancierCodes),
        productName,
        evaluatedCents: (c) => evaluatedCodeCents(queryClient, sessionId, c),
    });
    const held = [...salePromotionsOf(session, { facts, productName }), ...(code ? [code] : [])];
    const promos = [
        ...held,
        ...releasedPromotionsOf(session, released, held, { facts, productName }),
    ];
    return trackTotalCents(
        saleTotalCents,
        promos,
        track,
        (lineId) => session.reservedLines?.find((l) => l.lineId === lineId)?.discountCents,
    );
}
